// «@»-ссылки: наберите «@» в тексте - появится список рисунков, таблиц, формул, листингов, приложений, разделов
// и источников с поиском (например «@рис 2», «@олифер 45» -> [3, с. 45]). Enter/Tab - вставить, ←/→ - вариант
// фразы («на рисунке 3», «(рисунок 3)», только номер), Esc - оставить «@» как есть.
import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Extension } from '@tiptap/core';
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state';
import type { EditorView } from '@tiptap/pm/view';
import { numberingStore } from '../../core/editor/numberingPlugin';
import { ctx } from '../../core/ctx';
import { Icon } from '../../ui/icons';
import { overlay, useOverlay, type MentionState } from './overlay';
import { allTargets, defaultPhrase, phraseText, PHRASES, refContent, TargetThumb, KIND_NAMES, type Target } from './refs';
import { prefs } from './profile';
import { NB } from './data';

export interface MentionItem { type: 'target' | 'source'; id: string; target?: Target; num?: number; text: string; loc?: string }

interface MState { active: boolean; from: number }
export const mentionKey = new PluginKey<MState>('stoMention');

const KIND_HINT: [RegExp, string][] = [
  [/(рисун[а-яё]*|рис\.)[\s ]*$/i, 'fig'], [/(таблиц[а-яё]*|табл\.)[\s ]*$/i, 'tbl'], [/формул[а-яё]*[\s ]*$/i, 'eq'],
  [/листинг[а-яё]*[\s ]*$/i, 'lst'], [/приложени[а-яё]*[\s ]*$/i, 'app'], [/(раздел[а-яё]*|подраздел[а-яё]*|пункт[а-яё]*)[\s ]*$/i, 'sec'],
];

function beforeAt(view: EditorView, from: number): string {
  const $p = view.state.doc.resolve(from);
  return $p.parent.textBetween(Math.max(0, $p.parentOffset - 40), $p.parentOffset, ' ', '￼');
}

/** Кандидаты для запроса q (после «@»). */
export function mentionItems(query: string, before: string): MentionItem[] {
  const nb = numberingStore.get();
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  const hintKind = KIND_HINT.find(([re]) => re.test(before))?.[1];
  const out: MentionItem[] = [];
  const targets = allTargets(nb).filter((t) => t.kind !== 'struct');
  const tMatch = (t: Target) => {
    const hay = `${t.info.label} ${t.info.num} ${t.info.title} ${KIND_NAMES[t.kind]}`.toLowerCase().replace(/ /g, ' ');
    return tokens.every((tk) => hay.includes(tk) || (/^\d/.test(tk) && (t.info.num === tk || t.info.num.endsWith('.' + tk))));
  };
  const ts = targets.filter(tMatch);
  // порядок: подсказанный словом вид; затем объекты после курсора (ссылка ставится до объекта, 4.5.4) - ближайшие
  // первыми; рисунки/таблицы/формулы раньше разделов; затем объекты выше курсора
  const ed = ctx.editor;
  const pos = new Map<string, number>();
  const cur = ed ? ed.state.selection.from : 0;
  ed?.state.doc.descendants((n, p) => { if (n.attrs?.id) pos.set(n.attrs.id, p); return !n.isAtom; });
  const KIND_RANK: Record<string, number> = { fig: 0, tbl: 0, eq: 0, lst: 0, app: 1, sec: 2 };
  const rank = (t: Target) => {
    const p = pos.get(t.id) ?? 0;
    return [hintKind && t.kind === hintKind ? 0 : 1, KIND_RANK[t.kind] ?? 3, p >= cur ? 0 : 1, p >= cur ? p - cur : cur - p];
  };
  ts.sort((a, b) => { const x = rank(a); const y = rank(b); for (let i = 0; i < x.length; i++) if (x[i] !== y[i]) return x[i] - y[i]; return 0; });
  for (const t of ts) out.push({ type: 'target', id: t.id, target: t, text: t.info.title });
  // источники: последнее число в запросе - страница («@олифер 45» -> [N, с. 45])
  const words = tokens.filter((x) => !/^\d+(?:[–-]\d+)?$/.test(x));
  const pages = tokens.length > words.length ? tokens[tokens.length - 1] : '';
  if (!hintKind) {
    for (const [id, text] of nb.sourceList) {
      const hay = text.toLowerCase();
      if (!words.every((w) => hay.includes(w))) continue;
      if (!words.length && pages && tokens.length === 1) continue;
      out.push({ type: 'source', id, num: nb.sources.get(id), text, loc: pages && words.length ? 'с.' + NB + pages.replace('-', '–') : '' });
    }
  }
  return out.slice(0, 60);
}

function insertItem(view: EditorView, m: MentionState, item: MentionItem) {
  const ed = ctx.editor;
  if (!ed) return;
  const range = { from: m.from, to: m.to };
  if (item.type === 'target' && item.target) {
    const before = beforeAt(view, m.from);
    const phrase = m.phrase || defaultPhrase(item.target.kind, before);
    prefs.setPhrase(item.target.kind, phrase);
    // фраза с предлогом, который уже набран перед «@» («…показан на @» + «на рисунке 1»), - без повтора предлога
    const ph = (PHRASES[item.target.kind] || []).find((x) => x[0] === phrase);
    const lead = ph?.[1]?.split(' ')[0] || '';
    if (lead && ph![1].includes(' ')) {
      const trimmed = before.replace(/[\s\u00a0]+$/, '');
      const at = trimmed.length - lead.length;
      if (at >= 0 && trimmed.length < before.length && trimmed.slice(at).toLowerCase() === lead.toLowerCase() && (at === 0 || /[\s(\u00a0]/.test(trimmed[at - 1]))) {
        range.from = m.from - (before.length - at);
      }
    }
    ed.chain().focus().insertContentAt(range, refContent(item.target.kind, phrase, item.id)).run();
  } else {
    ed.chain().focus().insertContentAt(range, { type: 'cite', attrs: { items: [{ source: item.id, loc: item.loc || '' }] } }).run();
  }
  close(view);
}

function close(view: EditorView) {
  view.dispatch(view.state.tr.setMeta(mentionKey, { active: false, from: 0 }));
}

function phrasesFor(item: MentionItem | undefined): string[] {
  return item?.type === 'target' && item.target ? (PHRASES[item.target.kind] || []).map((p) => p[0]) : [];
}

export const MentionExtension = Extension.create({
  name: 'stoMention',
  priority: 850,
  addProseMirrorPlugins() {
    return [new Plugin<MState>({
      key: mentionKey,
      state: {
        init: () => ({ active: false, from: 0 }),
        apply: (tr, prev, _old, state) => {
          const meta = tr.getMeta(mentionKey) as MState | undefined;
          if (meta) return meta;
          if (!prev.active) return prev;
          const from = tr.mapping.map(prev.from);
          const sel = state.selection;
          if (!sel.empty || state.doc.textBetween(from, Math.min(from + 1, state.doc.content.size)) !== '@') return { active: false, from: 0 };
          const $a = state.doc.resolve(from);
          if (sel.head <= from || sel.head > from + 41 || sel.$head.parent !== $a.parent) return { active: false, from: 0 };
          return { active: true, from };
        },
      },
      view: () => ({
        update: (view) => {
          const st = mentionKey.getState(view.state)!;
          const cur = overlay.getState().mention;
          if (!st.active) { if (cur) overlay.getState().set({ mention: null }); return; }
          const head = view.state.selection.head;
          const query = view.state.doc.textBetween(st.from + 1, head, ' ', '');
          let c: { left: number; top: number; bottom: number };
          try { c = view.coordsAtPos(st.from); } catch { return; }
          const same = cur && cur.from === st.from && cur.query === query;
          overlay.getState().set({ mention: { from: st.from, to: head, query, left: c.left, top: c.top, bottom: c.bottom,
            index: same ? cur!.index : 0, phrase: same ? cur!.phrase : null } });
        },
        destroy: () => overlay.getState().set({ mention: null }),
      }),
      props: {
        handleTextInput: (view, from, to, text) => {
          if (text !== '@' || from !== to) return false;
          const $f = view.state.doc.resolve(from);
          if ($f.parent.type.spec.code || $f.marks().some((m) => m.type.name === 'code' || m.type.name === 'link')) return false;
          const prev = $f.parent.textBetween(Math.max(0, $f.parentOffset - 1), $f.parentOffset, ' ', '￼');
          if (prev && !/[\s( «"„]/.test(prev)) return false;          // e-mail «user@mail» - не ссылка
          // курсор - сразу после «@» в месте ввода: состояние выделения могло отстать от щелчка (щелчок сразу после
          // закрытия другой всплывающей панели), и набор после «@» ушёл бы в прежнее место
          const tr = view.state.tr.insertText('@', from, to).setMeta(mentionKey, { active: true, from });
          tr.setSelection(TextSelection.create(tr.doc, from + 1));
          view.dispatch(tr.scrollIntoView());
          return true;
        },
        handleKeyDown: (view, e) => {
          const st = mentionKey.getState(view.state);
          const m = overlay.getState().mention;
          if (!st?.active || !m) return false;
          const items = mentionItems(m.query, beforeAt(view, m.from));
          const set = (p: Partial<MentionState>) => overlay.getState().set({ mention: { ...m, ...p } });
          if (e.key === 'Escape') { close(view); return true; }
          if (!items.length) return false;
          const item = items[Math.min(m.index, items.length - 1)];
          if (e.key === 'ArrowDown') { set({ index: (m.index + 1) % items.length, phrase: null }); return true; }
          if (e.key === 'ArrowUp') { set({ index: (m.index - 1 + items.length) % items.length, phrase: null }); return true; }
          const ph = phrasesFor(item);
          if ((e.key === 'ArrowRight' || e.key === 'ArrowLeft') && ph.length) {
            const curPh = m.phrase || defaultPhrase(item.target!.kind, beforeAt(view, m.from));
            const i = ph.indexOf(curPh);
            set({ phrase: ph[(i + (e.key === 'ArrowRight' ? 1 : -1) + ph.length) % ph.length] });
            return true;
          }
          if (e.key === 'Enter' || e.key === 'Tab') { insertItem(view, m, item); return true; }
          return false;
        },
      },
    })];
  },
});

// ------------------------------------------------------------------------------------------------ список
export function MentionPopup() {
  const m = useOverlay((s) => s.mention);
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => { listRef.current?.querySelector('.is-sel')?.scrollIntoView({ block: 'nearest' }); }, [m?.index]);
  const ed = ctx.editor;
  if (!m || !ed) return null;
  const before = beforeAt(ed.view, m.from);
  const items = mentionItems(m.query, before);
  const idx = Math.min(m.index, Math.max(0, items.length - 1));
  const cur = items[idx];
  const below = m.bottom + 320 < window.innerHeight;
  const style: React.CSSProperties = { left: Math.min(m.left, window.innerWidth - 470), ...(below ? { top: m.bottom + 4 } : { bottom: window.innerHeight - m.top + 4 }) };
  return createPortal(
    <div className="mention-pop" style={style} data-testid="mention-pop" onMouseDown={(e) => e.preventDefault()}>
      <div className="mention-list" ref={listRef}>
        {!items.length && <div className="muted pad">Нет совпадений для «{m.query}». Esc - оставить «@».</div>}
        {items.map((it, i) => (
          <div key={it.type + it.id} className={'mention-row' + (i === idx ? ' is-sel' : '')} data-testid="mention-item"
            onMouseEnter={() => overlay.getState().set({ mention: { ...m, index: i, phrase: i === idx ? m.phrase : null } })}
            onClick={() => insertItem(ed.view, { ...m, index: i }, it)}>
            {it.type === 'target' && it.target ? <>
              <TargetThumb t={it.target} />
              <b>{it.target.kind === 'sec' ? it.target.info.num : it.target.info.label}</b>
              <span className="mention-title">{it.target.kind === 'eq' ? <code>{it.text}</code> : (it.text || <span className="muted">без названия</span>)}</span>
            </> : <>
              <span className="xref-thumb xref-thumb-ico"><Icon name="cite" size={16} /></span>
              <b>[{it.num}{it.loc ? ', ' + it.loc : ''}]</b>
              <span className="mention-title">{it.text}</span>
            </>}
          </div>
        ))}
      </div>
      {cur?.type === 'target' && cur.target && (
        <div className="phrase-pick">
          {(PHRASES[cur.target.kind] || []).map(([id]) => {
            const active = (m.phrase || defaultPhrase(cur.target!.kind, before)) === id;
            return <span key={id} className={'chip' + (active ? ' is-active' : '')}
              onClick={() => overlay.getState().set({ mention: { ...m, index: idx, phrase: id } })}>{phraseText(cur.target!.kind, id, cur.target!.info)}</span>;
          })}
        </div>
      )}
      <div className="mention-foot muted">↑↓ выбор · ←→ фраза · Enter вставить · Esc закрыть · «@олифер 45» - [N, с. 45]</div>
    </div>, document.body);
}
